import { apiFetch } from "../utils/apiFetch";
import { useEffect, useRef, useState } from "react";
import { FiUser, FiLoader, FiLogOut, FiXCircle, FiCreditCard, FiBriefcase } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import CancelMembershipPopup from "./CancelMembershipPopup";
import { useNavigate } from "react-router";
import ThemeToggle from "./ThemeToggle";
import { clearDisplayUser } from "../utils/authStorage";

const AccountMenu = () => {
  const { userInfo, setUserId, setUserInfo } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [cancellation, setCancellation] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const ended = ["cancelled", "completed", "expired"].includes(subscription?.status);
  const pending = ["requested", "confirmed", "unconfirmed"].includes(cancellation?.state);
  const logout = async () => {
    if (signingOut) return;
    setSigningOut(true);
    setError("");
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/auth/logout`, { method: "POST" });
      if (!response.ok) throw new Error("Could not sign out. Please try again.");
      clearDisplayUser();
      setUserId(null);
      setUserInfo(null);
      navigate("/", { replace: true });
    } catch (error) { setError(error.message); }
    finally { setSigningOut(false); }
  };

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const load = async () => {
      try {
        const response = await apiFetch(`${process.env.API_URL}/api/subscriptions/me`, {
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Could not load membership");
        if (!controller.signal.aborted) {
          setSubscription(data.subscription);
          setCancellation(data.cancellation);
        }
      } catch (error) {
        if (!controller.signal.aborted) setError(error.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [open]);

  useEffect(() => {
    if (!open || showPopup) return;
    const outside = (event) => { if (!containerRef.current?.contains(event.target)) setOpen(false); };
    const escape = (event) => { if (event.key === "Escape") { setOpen(false); buttonRef.current?.focus(); } };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open, showPopup]);

  const cancel = async () => {
    if (busy || pending || ended || !subscription?.providerSubscriptionId) return;
    setBusy(true);
    setError("");
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/subscriptions/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subscriptionId: subscription.providerSubscriptionId }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.code === "CANCELLATION_UNCONFIRMED") {
          setCancellation({ state: "unconfirmed" });
          setShowPopup(false);
        }
        throw new Error(data.message || "Could not cancel membership");
      }
      setCancellation(data.cancellation || null);
      if (data.action === "subscription_ended") setSubscription(null);
      setNotice(data.message);
      setShowPopup(false);
    } catch (error) {
      setError(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={containerRef} className="relative shrink-0 text-gray-950" onClick={(event) => event.stopPropagation()}>
      <button ref={buttonRef} type="button" aria-label="Account menu" aria-expanded={open}
        onClick={() => setOpen(!open)} title="Account"
        className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white hover:bg-gray-50">
        <FiUser aria-hidden="true" className="h-5 w-5" />
      </button>
      {open && <div className="absolute right-0 top-full z-40 mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-lg">
        <p className="break-words font-semibold">{userInfo?.username || "Account"}</p>
        <p className="mb-3 break-words text-xs text-gray-500">{userInfo?.email}</p>
        <div className="mb-3 flex items-center justify-between border-y border-gray-200 py-2"><span className="font-medium">Appearance</span><ThemeToggle /></div>
        <button type="button" onClick={() => { setOpen(false); navigate("/owner-payouts"); }} className="flex min-h-10 w-full items-center gap-2 rounded-md px-2 text-left hover:bg-gray-50"><FiBriefcase />Owner payouts</button>
        {loading ? <div role="status" aria-label="Loading membership" className="flex h-10 items-center justify-center"><FiLoader className="h-5 w-5 animate-spin" /></div>
          : subscription?.providerSubscriptionId && !ended && !pending && <button type="button"
            onClick={() => { setError(""); setNotice(""); setShowPopup(true); }}
            className="flex min-h-10 w-full items-center gap-2 rounded-md px-2 text-left text-red-700 hover:bg-red-50">
            <FiXCircle aria-hidden="true" className="h-4 w-4 shrink-0" />Cancel membership
          </button>}
        {!loading && pending && !ended && <p role="status" className="my-2 text-xs leading-5 text-gray-600">
          {cancellation.state !== "confirmed" ? "Cancellation confirmation is pending. Contact support before retrying."
            : cancellation.atCycleEnd && cancellation.effectiveAt
              ? `Membership continues until ${new Date(cancellation.effectiveAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}. Renewal is cancelled.`
              : "Membership cancelled."}
        </p>}
        {notice && <p role="status" className="my-2 text-xs text-orange-700">{notice}</p>}
        {error && !showPopup && <p role="alert" className="my-2 text-xs text-red-700">{error}</p>}
        <button type="button" onClick={logout} disabled={signingOut} aria-busy={signingOut} className="mt-2 flex min-h-10 w-full items-center gap-2 border-t border-gray-100 px-2 text-left hover:bg-gray-50 disabled:cursor-wait">
          {signingOut ? <FiLoader aria-hidden="true" className="h-4 w-4 animate-spin" /> : <FiLogOut aria-hidden="true" className="h-4 w-4" />}Sign out
        </button>
      </div>}
      {showPopup && <CancelMembershipPopup endDate={subscription?.currentPeriodEnd} busy={busy}
        error={error} onClose={() => setShowPopup(false)} onConfirm={cancel} />}
    </div>
  );
};

export default AccountMenu;
